/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import moment from 'moment';
import React from 'react';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import noDataIcon from '../../../images/IconaccountLimit.svg';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import { ScopeOneData } from './Scope1Table';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import { MAGIC_VALUE, parseIntBase } from '../../shared/Utils/Constants';
import { Checkbox, Divider, Input, Popup } from '@scuf/common';
import './Scope1TableTheme.scss';
import { v4 as uuid4 } from 'uuid';
import FilterScope1Actions from '../../../context/FilterScope1Context/FilterScope1Actions';
import { ScopeTwoData } from './Scope2Table';
import { fuelGasTypeLabelCheck } from '../AddFuelModal/AddFuelUtils';
import descriptors from '../../../locales/en-US/descriptors';

import { TFunction } from '@scuf/localization/dist/types';
import { useTranslation } from '@scuf/localization';

interface IColumnConfig {
  header: string;
  field: string;
  width: string;
  align?: 'left' | 'center';
  renderer: (celldata: ICellData) => JSX.Element;
}

export const getColumnDetails = (
  selectedYear: number,
  scopeDetails: string | undefined,
  t: TFunction,
  lng: string,
  selectYear?: string
): IColumnConfig[] => {
  const d = descriptors.energyManagement;
  moment.locale(lng);
  const localizedMonths = moment.localeData().monthsShort();
  const scope1TableColumns = localizedMonths.map((month, index) => {
    return {
      header:
        index === MAGIC_VALUE.ZERO || index === MAGIC_VALUE.ELEVEN
          ? `${month} ${isNaN(selectedYear) ? selectYear : selectedYear}`
          : `${month}`,
      field: month,
      width: `7%`,
      align: 'center' as IAny,
      renderer:
        scopeDetails === 'ScopeError'
          ? renderMonthlyEmissionScopeOneError
          : renderMonthlyEmission
    };
  });
  scope1TableColumns.unshift({
    header: '',
    field: '',
    width: '12%',
    align: 'left',
    renderer: renderCellQuantity
  });

  scope1TableColumns.unshift({
    header: t(d.FUEL_NAME),
    field: 'fuelName',
    width: '16%',
    align: 'left',
    renderer: renderCell
  });
  return scope1TableColumns as IAny;
};

export const formatData = (
  dispatch: () => {},
  year: number,
  data: ScopeOneData[]
) => {
  return data.map((fuelData) => {
    let data = {
      fuelName: fuelData?.fuelName,
      dispatch: dispatch,
      selectedYear: year,
      unit: fuelData?.unit,
      Co2: fuelData?.emissionFactorValue,
      emissionFactorUnitByCo2eMeasurement:
        fuelData?.emissionFactorUnitByCo2eMeasurement,
      fuelConsumpId: fuelData?.fuelConsumptionId,
      fuleTypeId: fuelData?.fuleTypeId,
      emissionFactorId: fuelData?.emissionFactorId
    };
    fuelData?.monthWiseFuelConsumptions?.forEach((monthData) => {
      data = {
        ...data,
        [monthData.month.substring(0, MAGIC_VALUE.THREE)]: {
          quantity:
            monthData.quantity === 0 &&
            monthData.fuelConsumptionId ===
              '00000000-0000-0000-0000-000000000000'
              ? null
              : monthData.quantity,
          isFutureMonth: isFutureMonth(
            monthData.month.substring(0, MAGIC_VALUE.THREE),
            fuelData.year
          ),
          year: fuelData.year,
          fuelConsumptionId: monthData.fuelConsumptionId,
          isPopupOpen: false,
          changedMonthVal: false,
          id: uuid4()
        }
      };
    });
    return data;
  });
};

export const popupOpenCheck = (option: string) =>
  option === 'show' ? true : false;

export const renderMonthlyEmission = (celldata: ICellData) => {
  const {
    rowData,
    rowData: { dispatch }
  } = celldata;
  const payload = {
    ...rowData,
    selectedMonth: moment()
      .month(celldata.field)
      .format('MMMM'),
    // selectedQuantity: celldata.value?.quantity,
    // fuelConsumpId: celldata.value?.fuelConsumptionId,
    selectedQuantity: celldata ? celldata.value?.quantity : null,
    fuelConsumpId: celldata ? celldata.value?.fuelConsumptionId : null,
    fuleTypeId: rowData.fuleTypeId,
    emissionFactorId: rowData.emissionFactorId,
    // Co2: rowData.Co2
    Co2: rowData ? rowData.Co2 : null
  };
  if (celldata?.value?.isFutureMonth) {
    return (
      <>
        <div className="cef-row">
          <div className="row3">-</div>
          <div
            className={`row2 ${
              celldata.field == 'Jun'
                ? 'displayCOTwoValueDisplay'
                : 'displayCOTwoValue'
            }`}
          >
            {celldata.field == 'Jun' && celldata.rowData?.Co2
              ? `${Math.round(celldata.rowData?.Co2 * 100) / 100} (${
                  celldata.rowData.emissionFactorUnitByCo2eMeasurement ===
                    null ||
                  celldata.rowData.emissionFactorUnitByCo2eMeasurement ===
                    undefined
                    ? '--'
                    : celldata.rowData.emissionFactorUnitByCo2eMeasurement
                })`
              : ''}
          </div>
        </div>
      </>
    );
  } else if (celldata.value?.quantity === null) {
    return (
      <>
        <div className="cef-row no-data-bg-div">
          <div
            className="no-data-bg row-value"
            onClick={() =>
              dispatch(FileUploadAction.setMissingFuelModal(payload))
            }
          >
            <img className="no-missing-img" src={noDataIcon} height="1rem" />
          </div>
          <div
            className={`row2 ${
              celldata.field == 'Jun'
                ? 'displayCOTwoValueDisplay'
                : 'displayCOTwoValue'
            }`}
          >
            {celldata.field == 'Jun'
              ? `${Math.round(celldata.rowData?.Co2 * 100) / 100} (${
                  celldata.rowData.emissionFactorUnitByCo2eMeasurement ===
                    null ||
                  celldata.rowData.emissionFactorUnitByCo2eMeasurement ===
                    undefined
                    ? '--'
                    : celldata.rowData.emissionFactorUnitByCo2eMeasurement
                })`
              : ''}
          </div>
        </div>
      </>
    );
  } else {
    return (
      <>
        <div className="cef-row">
          <div
            className="row-value"
            onClick={() => dispatch(FileUploadAction.setEditFuelModal(payload))}
          >
            {celldata.value?.quantity}
          </div>
          <div
            className={`row2 ${
              celldata.field == 'Jun'
                ? 'displayCOTwoValueDisplay'
                : 'displayCOTwoValue'
            }`}
          >
            {celldata.field == 'Jun'
              ? `${Math.round(celldata.rowData?.Co2 * 100) / 100} (${
                  celldata.rowData.emissionFactorUnitByCo2eMeasurement ===
                    null ||
                  celldata.rowData.emissionFactorUnitByCo2eMeasurement ===
                    undefined
                    ? '--'
                    : celldata.rowData.emissionFactorUnitByCo2eMeasurement
                })`
              : ''}
          </div>
        </div>
      </>
    );
  }
};

export const noFuelConsumedCheck = (
  noFuel: IAny,
  setcellData: IAny,
  setNoFuel: IAny
) => {
  if (noFuel) {
    setcellData('');
    setNoFuel(false);
  } else {
    setcellData('0');
    setNoFuel(true);
  }
};

export const getUpdatedTabledata = (
  rowData: IAny,
  tableData: IAny[],
  SetHideFooter: IAny,
  payload: IAny,
  quantityVal: IAny
) => {
  return tableData?.map((val) => {
    Object.keys(val).forEach((key) => {
      if (val[key]?.id === payload?.id) {
        SetHideFooter(false);
        val[key].isPopupOpen = true;
        val[key].changedMonthVal = true;
        val[key].quantity = quantityVal;
      }
    });
    return val;
  });
};

const missingDataInputCheck = (val: IAny, setcellData: (arg0: any) => void) => {
  const re = /^[0-9\b]+$/;
  if (val === '' || re.test(val)) {
    if (val.length <= MAGIC_VALUE.FIVE || val.toString() === '') {
      setcellData(val);
    }
  }
};

export const renderMonthlyEmissionScopeOneError = (
  celldata: ICellData,
  tableData: IAny[],
  setTableData: Function,
  quantityVal: IAny,
  setcellData: IAny,
  SetHideFooter: IAny,
  setNoFuel: IAny,
  noFuel: IAny
) => {
  const {
    rowData,
    rowData: { dispatch }
  } = celldata;

  const payload = {
    ...rowData,
    selectedMonth: moment()
      .month(celldata.field)
      .format('MMMM'),
    selectedQuantity: celldata.value?.quantity,
    fuelConsumpId: celldata.value?.fuelConsumptionId,
    fuleTypeId: rowData.fuleTypeId,
    emissionFactorId: rowData.emissionFactorId,
    id: celldata.value?.id
  };
  if (celldata.value?.isFutureMonth) {
    return <>-</>;
  } else if (celldata.value?.quantity === null) {
    const missingPopupTabledata = (
      tableData: IAny[],
      SetHideFooter: IAny,
      option: string
    ) => {
      return tableData.map((val) => {
        Object.keys(val).forEach((key) => {
          if (val[key]?.id === payload?.id) {
            val[key].changedMonthVal === 'false' && SetHideFooter(true);
            val[key].isPopupOpen = popupOpenCheck(option);
          } else if (typeof val[key] === 'object') {
            val[key].isPopupOpen = false;
          } else {
            console.log('');
          }
        });
        return val;
      });
    };
    return (
      <Popup
        className="popup-theme-wrap missing-data-popup"
        open={celldata.value.isPopupOpen}
        element={
          <div
            className="no-data-bg"
            onClick={() => {
              setcellData('');
              setNoFuel(false);
              const updatedTableData = missingPopupTabledata(
                tableData,
                SetHideFooter,
                'show'
              );
              setTableData(updatedTableData);
              dispatch(FilterScope1Actions.saveErrorModal(updatedTableData));
            }}
          >
            <img className="no-missing-img" src={noDataIcon} height="1rem" />
          </div>
        }
        on="click"
      >
        <div className="editPopup">
          <span className="missing-data-header">
            {moment()
              .month(celldata.field)
              .format('MMMM')}{' '}
            {celldata.value.year}
          </span>
          <div className="flexMissingDataContent">
            <Input
              className="missing-data-input"
              value={quantityVal}
              onChange={(val) => missingDataInputCheck(val, setcellData)}
              disabled={noFuel}
            />
            <img
              className="alarm-meter-icon"
              src={require('../../../images/closeIconMissingData.svg')}
              onClick={() => {
                const updatedTableData = missingPopupTabledata(
                  tableData,
                  SetHideFooter,
                  'hide'
                );
                setTableData(updatedTableData);
                dispatch(FilterScope1Actions.saveErrorModal(updatedTableData));
              }}
            />
            <img
              className="alarm-meter-icon"
              src={require('../../../images/submitIconMissingData.svg')}
              onClick={() => {
                if (quantityVal !== '') {
                  const updatedTableData = getUpdatedTabledata(
                    rowData,
                    tableData,
                    SetHideFooter,
                    payload,
                    quantityVal
                  );
                  setTableData(updatedTableData);
                  dispatch(
                    FilterScope1Actions.saveErrorModal(updatedTableData)
                  );
                }
              }}
            />
          </div>
          <Divider className="missing-data-divider" fitted />
          <div className="flexMissingDataContent">
            <Checkbox
              label="No Fuel Consumed"
              onChange={(checked) => {
                noFuelConsumedCheck(noFuel, setcellData, setNoFuel);
              }}
              checked={noFuel}
            />
          </div>
        </div>
      </Popup>
    );
  } else {
    return <div>{celldata.value?.quantity}</div>;
  }
};
export const renderCell = (celldata: ICellData) => {
  const { rowData } = celldata;
  const unit = celldata.field.toLowerCase().includes('fuelname')
    ? `(${(UnitUtility as IAny).getUnitSymbol(rowData.unit?.toLowerCase())})`
    : '';
  return (
    <>
      {celldata.value} {unit}
    </>
  );
};

export const renderCellScope2 = (celldata: ICellData) => {
  const { rowData } = celldata;
  var emissionFactorUnit;
  const GuidEmpty = '00000000-0000-0000-0000-000000000000';
  let emissionFactorDetailsObject = rowData?.emissionData?.filter(
    (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
      val.siteId !== GuidEmpty &&
      moment(val?.effectiveFrom).year() === parseInt(celldata.field)
  );

  if (emissionFactorDetailsObject?.length > 0) {
    emissionFactorUnit =
      emissionFactorDetailsObject[0]?.emissionFactorUnitByCo2eMeasurement;
  } else {
    let emissionFactorDetailsDefaultWithregionId = rowData?.emissionData?.filter(
      (val: {
        regionId: boolean;
        siteId: string;
        effectiveFrom: moment.MomentInput;
      }) => val?.siteId === GuidEmpty && val?.regionId
    );
    if (emissionFactorDetailsDefaultWithregionId?.length > 0) {
      emissionFactorUnit =
        emissionFactorDetailsDefaultWithregionId[0]
          ?.emissionFactorUnitByCo2eMeasurement;
    } else {
      let emissionFactorDetailsDefaultWithoutregionId = rowData?.emissionData?.filter(
        (val: {
          regionId: boolean;
          siteId: string;
          effectiveFrom: moment.MomentInput;
        }) => val?.siteId === GuidEmpty && !val?.regionId
      )[0];
      emissionFactorUnit =
        emissionFactorDetailsDefaultWithoutregionId?.emissionFactorUnitByCo2eMeasurement;
    }
  }
  // const unit = celldata.field.toLowerCase().includes('fuelname')
  //   ? `(${(UnitUtility as IAny).getUnitSymbol(rowData.unit?.toLowerCase())})`
  //   : '';
  return (
    <>{`${celldata.value} (${
      emissionFactorUnit === undefined ? '-/-' : emissionFactorUnit
    })`}</>
  );
};

export const renderCellQuantity = (celldata: ICellData) => {
  return (
    <div className="cef-row">
      <div className="row1">Quantity</div>
      <div className="row2 displayCOTwoName">
        <i>
          CO<sub>2</sub>e Factor
        </i>
      </div>
    </div>
  );
};

export const getConnectorConfigCSVUpload = (
  pointHistoryUploadToggle?: boolean
) => {
  return [
    {
      name: 'upload-button-get-csv-files',
      options: {
        pageNumber: '1'
      }
    }
  ];
};

export const getConnectorConfig = (
  activeindex: number,
  selectedYear: string,
  toggleFeatureAllUpload?: boolean,
  toggleFeatureScopeOne?: boolean,
  toggleFeatureGAS?: boolean,
  toggleFeatureScopeTwo?: boolean,
  emaasContext?: IAny
) => {
  let categoryName;

  categoryName = fuelGasTypeLabelCheck(
    activeindex,
    'stationaryCombustion',
    'refrigerants',
    toggleFeatureAllUpload,
    toggleFeatureScopeOne,
    toggleFeatureScopeTwo,
    'scope2',
    toggleFeatureGAS,
    'GAS',
    'dataUpload'
  );

  if (categoryName === 'dataUpload') {
    return [
      {
        name: 'manual-upload-get-files',
        options: {
          pageNumber: '1'
        }
      },
      {
        name: 'get-missing-data-details',
        options: {
          year: selectedYear
        }
      }
    ];
  } else if (categoryName === 'stationaryCombustion') {
    return [
      {
        name: 'get-stationary-combustion',
        options: {
          year: selectedYear,
          combustionType: 'Stationary Combustion'
        }
      },
      {
        name: 'get-missing-data-details',
        options: {
          year: selectedYear,
          combustionType: 'Stationary+Combustion'
        }
      }
    ];
  } else if (categoryName === 'refrigerants') {
    return [
      {
        name: 'get-stationary-combustion',
        options: {
          year: selectedYear,
          combustionType: 'Refrigerants'
        }
      },
      {
        name: 'get-missing-data-details',
        options: {
          year: selectedYear,
          combustionType: 'Refrigerants'
        }
      }
    ];
  } else if (categoryName === 'scope2') {
    return [
      {
        name: 'manual-upload-add_fules_dropdown',
        options: {
          combustionName: 'Scope2'
        }
      },
      {
        name: 'get-missing-data-details',
        options: {
          year: selectedYear,
          combustionType: 'Scope2'
        }
      }
    ];
  } else if (categoryName === 'GAS') {
    return [
      {
        name: 'manual-upload-add_fules_dropdown',
        options: {
          combustionName: 'Scope2'
        }
      },
      {
        name: 'get-missing-data-details',
        options: {
          year: selectedYear,
          combustionType: 'Scope2'
        }
      }
    ];
  } else
    return [
      {
        name: 'no-defined'
      }
    ];
};

export const getConnectorConfigScopeOneError = (
  activeindex: number,
  selectedYear: string
) => {
  if (activeindex === MAGIC_VALUE.ZERO) {
    return {
      name: 'get-stationary-combustion',
      options: {
        year: selectedYear,
        combustionType: 'Stationary Combustion'
      }
    };
  } else if (activeindex === MAGIC_VALUE.ONE) {
    return {
      name: 'get-stationary-combustion',
      options: {
        year: selectedYear,
        combustionType: 'Refrigerants'
      }
    };
  } else
    return {
      name: 'no-defined'
    };
};

const isFutureMonth = (month: string, year: string) => {
  const currentYear = new Date().getFullYear();
  if (parseInt(year, parseIntBase) > currentYear) {
    return true;
  } else if (parseInt(year, parseIntBase) < currentYear) {
    return false;
  } else if (
    parseInt(
      moment()
        .month(month)
        .format('M'),
      parseIntBase
    ) >
    new Date().getMonth() + 1
  ) {
    return true;
  } else {
    return false;
  }
};

export const getYearValues = () => {
  var years = Array.from(Array(new Date().getFullYear() - 2020), (_, i) =>
    (i + 2021).toString()
  );
  return years.map((years) => {
    return { value: years, text: years };
  });
};

export const getScope2Values = (val: any[]) => {
  return val[0]?.fuelTypeUnits?.map(
    (unitValue: { fuelTypeUnitId: any; unit: any }) => {
      return { value: unitValue.unit, text: unitValue.unit };
    }
  );
};

export const getScope2FirstVal = (
  val: any[],
  selectedUnit: string | undefined
) => {
  return val[0]?.fuelTypeUnits?.filter(
    (unitValue: { unit: string }) => unitValue.unit === selectedUnit
  )[0].fuelTypeUnitId;
};
export const GuidEmpty = '00000000-0000-0000-0000-000000000000';
export const getupdatedSiteIDWithDetails = (
  listOfUtilityTypes: any[],
  setSelectedUnit?: any
) => {
  let EmissionFactor;
  let emissionFactorDetailsObjectCount = listOfUtilityTypes[0]?.emissionFactor?.filter(
    (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
      val.siteId !== GuidEmpty
  );
  let allTheObjectAreKWHCheck = emissionFactorDetailsObjectCount?.every(
    (val: { emissionFactorUnitByCo2eMeasurement: string }) =>
      val.emissionFactorUnitByCo2eMeasurement === 'kgCo2e/kwh'
  );

  if (
    emissionFactorDetailsObjectCount?.length > 1 &&
    !allTheObjectAreKWHCheck
  ) {
    EmissionFactor = listOfUtilityTypes[0]?.emissionFactor?.filter(
      (val: {
        emissionFactorUnitByCo2eMeasurement: string;
        siteId: string;
        effectiveFrom: moment.MomentInput;
      }) =>
        val.siteId !== GuidEmpty &&
        val.emissionFactorUnitByCo2eMeasurement !== 'kgCo2e/kwh'
    );
  } else {
    EmissionFactor = listOfUtilityTypes[0]?.emissionFactor?.filter(
      (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
        val.siteId !== GuidEmpty
    );
  }

  let lengthOfEmissionFactor = EmissionFactor?.length > 0;
  if (lengthOfEmissionFactor) {
    let unit =
      EmissionFactor?.length > 0 &&
      EmissionFactor[0].emissionFactorUnitByCo2eMeasurement;
    setSelectedUnit && setSelectedUnit(unit.toString().split('/')[1]);
  }

  return lengthOfEmissionFactor;
};

export const isDataAvailableCheck = (fileRecords: IAny) => {
  return fileRecords ? true : false;
};

export const dataAvailableCondition = (
  isDataAvailable: any,
  error: any,
  status: any,
  loading: any,
  fileRecords: any,
  setData: (arg0: any[]) => void,
  setErrorInfo: (arg0: any) => void
) => {
  if ((isDataAvailable || error || status) && !loading) {
    if (isDataAvailable) {
      setData([...fileRecords]);
    } else {
      setData([]);
    }
    setErrorInfo(error);
  }
};

export const getColumnDetailsScopeTwo = (
  //selectedYear: number,
  scopeDetails: string | undefined,
  t: TFunction
): IColumnConfig[] => {
  let currentYear = moment().year();
  const firstYear = 2021;
  const years: any = [];
  for (let i = currentYear; i >= firstYear; i--) {
    years.push(i);
  }
  const scope2TableColumns = years.map((year: any) => {
    return {
      header: year,
      field: year,
      width: '7%',
      align: 'center' as IAny,
      renderer:
        scopeDetails === 'ScopeError'
          ? renderMonthlyEmissionScopeOneError
          : renderYearlyEmissionScope2
    };
  });
  scope2TableColumns.unshift({
    header: '',
    field: 'CO2emission',
    width: '10%',
    align: 'left',
    renderer: renderCO2Name
  });
  const d = descriptors.energyManagement;

  scope2TableColumns.unshift({
    header: t(d.UTILITY_TYPES),
    field: 'fuelName',
    width: '16%',
    align: 'left',
    renderer: renderCellScope2
  });
  return scope2TableColumns as IAny;
};
export const renderCO2Name = (celldata: ICellData) => {
  return (
    <div className="displayCOTwoNameScope2">
      <i>
        CO<sub>2</sub>e Factor
      </i>
    </div>
  );
};

export const renderYearlyEmissionScope2 = (celldata: ICellData) => {
  const {
    rowData,
    rowData: { dispatch }
  } = celldata;
  const payload = {
    ...rowData,
    // selectedQuantity: celldata?.value?.quantity,
    // fuelConsumpId: celldata?.value?.fuelConsumptionId,
    fuleTypeId: rowData.fuleTypeId,
    emissionFactorId: rowData.emissionFactorId,
    // Co2: rowData?.Co2,
    // year: rowData?.year
    selectedQuantity: celldata ? celldata.value?.quantity : null,
    fuelConsumpId: celldata ? celldata.value?.fuelConsumptionId : null,
    Co2: rowData ? rowData.Co2 : null,
    year: rowData ? rowData.year : null
  };
  const GuidEmpty = '00000000-0000-0000-0000-000000000000';
  var year = celldata.field;
  var emissionValue;

  let emissionFactorDetailsObject = rowData?.emissionData?.filter(
    (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
      val.siteId !== GuidEmpty &&
      moment(val?.effectiveFrom).year() === parseInt(celldata.field)
  );

  if (emissionFactorDetailsObject?.length > 0) {
    emissionValue = emissionFactorDetailsObject[0]?.emissionFactorValue;
  } else {
    let emissionFactorDetailsDefaultWithregionId = rowData?.emissionData?.filter(
      (val: {
        regionId: boolean;
        siteId: string;
        effectiveFrom: moment.MomentInput;
      }) => val?.siteId === GuidEmpty && val?.regionId
    );
    if (emissionFactorDetailsDefaultWithregionId?.length > 0) {
      emissionValue =
        emissionFactorDetailsDefaultWithregionId[0]?.emissionFactorValue;
    } else {
      let emissionFactorDetailsDefaultWithoutregionId = rowData?.emissionData?.filter(
        (val: {
          regionId: boolean;
          siteId: string;
          effectiveFrom: moment.MomentInput;
        }) => val?.siteId === GuidEmpty && !val?.regionId
      )[0];
      emissionValue =
        emissionFactorDetailsDefaultWithoutregionId?.emissionFactorValue;
    }
  }
  return (
    <>
      <div>{emissionValue === undefined ? '--' : emissionValue}</div>
    </>
  );
};

export const formatDataScope2 = (data: ScopeTwoData[]) => {
  return data?.map((fuelData) => {
    let data = {
      fuelName: fuelData.fuelType?.fuelTypeName,
      scopeType: fuelData.fuelType?.scopeType,
      emissionData: fuelData.emissionFactor
    };
    return data;
  });
};
